/**
 * Single source of truth for turning a stored IScore into the per-show-type
 * *display values* (placement, score, deductions, times, faults, etc.).
 *
 * Ported verbatim from the web `getDataRows` so the web Results grid and the
 * mobile Scoring list render identical values. No `moment` dependency — the
 * time formatters below produce the same output as the previous
 * `moment.utc(...).format(...)` calls for all realistic (sub-hour) durations.
 */
import {
  IScore,
  IEventSummary,
  IRunOff,
  IObedienceScore,
  IRallyObedienceScore,
  IScentScore,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { isNonQualifyingResult } from "./helpers/result-type";
import { formatSecondsToClock } from "./helpers/time";
import {
  formatWeightPullPercent,
  formatWeightPullTime,
} from "./helpers/weight-pull";
import { calculateObedienceScore } from "./placement/obedience";
import {
  getAkcScentworkResult,
  getAkcScentworkFaults,
  getAkcScentworkPlacement,
  getAkcScentworkFieldValue,
} from "./placement/scentwork";
import {
  abiScentDogGamesGetPlacement,
  abiScentDogGamesGetResultLabel,
  formatSDGEffectiveTime,
} from "./placement/scent-dog-games";
import { bhCalculatePlacement, bhGetHeightDivision } from "./placement/barn-hunt";

// --- time formatting --------------------------------------------------------

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const formatJumpInches = (value?: number | null): string =>
  value ? `${value}"` : "";

const obedienceJumpHeight = (row: IScore): number | undefined => {
  const preferred = (row.dog_class?.level || "").includes("Preferred");
  return preferred
    ? (row.akc_obedience_preferred_jump_height ?? row.obedience_jump_height)
    : row.obedience_jump_height;
};

/** mm:ss (minutes wrap at 60, mirroring moment's `mm:ss`). */
const formatMMSS = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${pad2(Math.floor(s / 60) % 60)}:${pad2(s % 60)}`;
};

const formatRatRaceTime = (
  totalSeconds: number | undefined
): string | undefined => {
  if (totalSeconds === undefined || totalSeconds === null) return undefined;
  const s = totalSeconds;
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  const hundredths = Math.round((s % 1) * 100);
  return (
    String(mins).padStart(2, "0") +
    ":" +
    String(secs).padStart(2, "0") +
    ":" +
    String(hundredths).padStart(2, "0")
  );
};

const formatTimeWithMs = (
  time?: string | number,
  resultType?: string
): string => {
  // Show N/A for non-qualifying results that don't require time tracking
  if (resultType && isNonQualifyingResult(resultType)) {
    return "N/A";
  }

  if (!time) {
    return "";
  }

  return formatSecondsToClock(time);
};

// --- result type / label ----------------------------------------------------

const resultTypeLabels: any = {
  weighed: "weighed",
  complete: "complete",
  not_qualified: "nq",
  qualified: "qualified",
  disqualified: "disqualified",
  excused: "excused",
  absent: "absent",
  bitch_in_season: "ais",
  passed: "passed",
  failed: "failed",
  non_regular: "non-regular",
  scratched: "scratched",
  conflict: "conflict",
  Pass: "Pass",
  Fail: "Fail",
  Absent: "ABS",
  ABS: "ABS",
  na: "No Result",
};

const simpleHerdingShowTypes = [
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
];

/**
 * Resolve the canonical result_type for a score across every show type's
 * nested result object. Returns "na" when no result has been entered.
 */
export const getResultType = (result: any): string => {
  if (!result) {
    return "na";
  }
  if (result.status === Constants.score_status.scratched) {
    return Constants.score_result_type.scratched;
  }
  // Only treat as conflict when the status specifically says so.
  // A score that was previously flagged as conflict but has since had results
  // entered (status becomes "complete") should resolve using the nested result data.
  if (result.status === Constants.score_status.conflict) {
    return Constants.score_result_type.conflict;
  }
  if (result.nosework !== undefined) {
    return result.nosework.result_type;
  } else if (result.fetch !== undefined) {
    if (result.fetch.result_type) {
      switch (result.fetch.result_type) {
        case Constants.score_result_type.qualified: {
          return "passed";
        }
        case Constants.score_result_type.disqualified: {
          return "disqualified";
        }
        case Constants.score_result_type.absent: {
          return "absent";
        }
        case Constants.score_result_type.excused:
        case Constants.score_result_type.not_qualified: {
          return "failed";
        }
      }
    } else {
      return result.fetch.passed === true ? "passed" : "failed";
    }
  } else if (result.fastcat !== undefined) {
    return result.fastcat.result_type;
  } else if (result.cat !== undefined) {
    return result.cat.result_type;
  } else if (result.urban_rat_race !== undefined) {
    return result.urban_rat_race.result_type;
  } else if (result.country_rat_race !== undefined) {
    return result.country_rat_race.result_type;
  } else if (result.lost_and_found !== undefined) {
    return result.lost_and_found.result_type;
  } else if (result.rally_obedience !== undefined) {
    return result.rally_obedience.result_type;
  } else if (result.obedience !== undefined) {
    return result.obedience.result_type;
  } else if (result.scentwork !== undefined) {
    return result.scentwork.result_type;
  } else if (result.dock_jumping !== undefined) {
    return result.dock_jumping.result_type;
  } else if (result.shed_dog !== undefined) {
    return result.shed_dog.result_type;
  } else if (result.precision_coursing !== undefined) {
    return result.precision_coursing.result_type;
  } else if (result.weight_pull !== undefined) {
    if (result.weight_pull.result_type) {
      return result.weight_pull.result_type;
    }
    if (
      result.weight_pull.time &&
      result.weight_pull.time.toString().length > 0 &&
      result.weight_pull.time.toString() !== "NaN"
    ) {
      return "qualified";
    } else if (Number(result.weight_pull.dog_weight) > 0) {
      return "weighed";
    }
  } else if (result.farm_dog !== undefined) {
    return result.farm_dog.result_type;
  } else if (result.water_dog !== undefined) {
    return result.water_dog.result_type;
  } else if (result.agility !== undefined) {
    return result.agility.result_type;
  } else if (result.akc_agility !== undefined) {
    return result.akc_agility.result_type;
  } else if (result.herding !== undefined) {
    return result.herding.result_type;
  } else if (result.scent !== undefined) {
    return result.scent.result_type;
  } else if (result.barn_hunt !== undefined) {
    return result.barn_hunt.result_type;
  } else if (result.scent_dog_games !== undefined) {
    return result.scent_dog_games.result_type || "na";
  }

  return "na";
};

/** Human-readable result label (e.g. "qualified", "nq", "Pass"). */
export const getResultLabel = (row: IScore): string => {
  const resultType = getResultType(row);

  if (simpleHerdingShowTypes.includes(row.show_type)) {
    if (
      resultType === Constants.score_result_type.qualified ||
      resultType === "Pass" ||
      resultType === "passed"
    ) {
      return "Pass";
    }

    if (
      resultType === Constants.score_result_type.not_qualified ||
      resultType === "Fail" ||
      resultType === "failed"
    ) {
      return "Fail";
    }

    if (
      resultType === Constants.score_result_type.absent ||
      resultType === "Absent" ||
      resultType === "ABS"
    ) {
      return "Absent";
    }
  }

  return resultTypeLabels[resultType];
};

const getDeductions = (score?: IObedienceScore | IRallyObedienceScore) => {
  // Show N/A for non-qualifying results that don't require deduction tracking
  if (score?.result_type && isNonQualifyingResult(score.result_type)) {
    return "N/A";
  }

  if (score?.total_deductions_only) {
    return score.total_deductions;
  }

  return score?.element_deductions?.reduce(
    (sum: number, item: any) => sum + parseFloat(item.element_deductions ?? "0"),
    0
  );
};

const getScentsString = (result?: IScentScore): string => {
  if (!result) {
    return "";
  }

  const scents = [];

  if (result.scent1) scents.push("1");
  if (result.scent2) scents.push("2");
  if (result.scent3) scents.push("3");
  if (result.scent4) scents.push("4");

  return scents.join(", ");
};

const scoreIdOf = (value: unknown): string => (value != null ? String(value) : "");

const sameScoreRow = (a: IScore, b: IScore): boolean => {
  const aId = scoreIdOf(a._id);
  const bId = scoreIdOf(b._id);
  if (aId && bId) return aId === bId;
  return !!a.arm_number && a.arm_number === b.arm_number;
};

const numericObedienceScore = (score: IScore): number => {
  const value = calculateObedienceScore(score.obedience, Constants.show_type.obedience);
  return typeof value === "number" ? value : Number.NEGATIVE_INFINITY;
};

/** 1-based place within a resolved run-off, if this score is in that run-off. */
const runOffOrderForScore = (score: IScore, runOffs?: IRunOff[]): number | undefined => {
  const scoreId = scoreIdOf(score._id);
  for (const runOff of runOffs || []) {
    if (!runOff.resolved_at) continue;
    if (runOff.show_element !== score.dog_class?.show_element) continue;
    if (runOff.level !== score.dog_class?.level) continue;

    if (runOff.ordered_placements?.length) {
      const placed = runOff.ordered_placements.find(
        (p) => scoreId && scoreIdOf(p.score_id) === scoreId
      );
      if (placed) return placed.placement;
    }

    if (runOff.winner_arm_number && score.arm_number && runOff.winner_arm_number === score.arm_number) {
      return 1;
    }
  }
  return undefined;
};

// --- per-show-type display values ------------------------------------------

/**
 * Returns the show-type-specific display fields for a single score. Keys match
 * the web Results grid column fields exactly. Pass `allScores` (the scores for
 * the same show) to enable placement calculations, and `runOffs` for obedience
 * run-off resolution.
 */
export const getShowSpecificData = (
  showType: string,
  row: IScore,
  allScores?: IScore[],
  event?: IEventSummary,
  runOffs?: IRunOff[]
): Record<string, any> => {
  switch (showType) {
    case Constants.show_type.obedience: {
      // Calculate placement for AKC Obedience
      // Uses same logic as akcObedienceGetPlacement from backend
      let obediencePlacement = "";
      if (
        allScores &&
        (event?.sanctioning_club === Constants.sanctioning_club.AKC ||
          event?.sanctioning_club === Constants.sanctioning_club.UKC)
      ) {
        const isNQ = row.obedience?.total_deductions_only
          ? false
          : (row.obedience?.element_deductions || []).find((e) =>
              isNaN(parseFloat(e.element_deductions))
            );

        if (
          !isNQ &&
          row.obedience?.result_type === Constants.score_result_type.qualified
        ) {
          const sameClassScores = allScores
            .filter(
              (s) =>
                s.show_type === Constants.show_type.obedience &&
                s.show_id?.toString() === row.show_id?.toString() &&
                s.obedience?.result_type ===
                  Constants.score_result_type.qualified &&
                s.dog_class?.show_element === row.dog_class?.show_element &&
                s.dog_class?.level === row.dog_class?.level &&
                s.dog_class?.section === row.dog_class?.section &&
                !(s.obedience?.total_deductions_only
                  ? false
                  : (s.obedience?.element_deductions || []).find((e) =>
                      isNaN(parseFloat(e.element_deductions))
                    ))
            )
            .sort((a, b) => {
              const scoreA = numericObedienceScore(a);
              const scoreB = numericObedienceScore(b);
              if (scoreB !== scoreA) {
                return scoreB - scoreA;
              }

              const aRunOff = runOffOrderForScore(a, runOffs);
              const bRunOff = runOffOrderForScore(b, runOffs);
              if (aRunOff != null && bRunOff != null && aRunOff !== bRunOff) {
                return aRunOff - bRunOff;
              }
              if (aRunOff != null && bRunOff == null) return -1;
              if (aRunOff == null && bRunOff != null) return 1;

              return (
                (parseInt(a.arm_number || "999") || 999) -
                (parseInt(b.arm_number || "999") || 999)
              );
            });

          const placement = sameClassScores.findIndex((s) => sameScoreRow(s, row));

          if (placement !== -1 && placement < 4) {
            const placements = ["1st", "2nd", "3rd", "4th"];
            obediencePlacement = placements[placement];
          }
        }
      }

      return {
        section: row.dog_class?.section, // For C-WAGS Obedience
        totalDeductions: getDeductions(row.obedience),
        score: calculateObedienceScore(
          row.obedience,
          Constants.show_type.obedience
        ),
        placement: obediencePlacement,
        jumpHeight: formatJumpInches(obedienceJumpHeight(row)),
      };
    }
    case Constants.show_type.rally_obedience:
      return {
        section: row.dog_class?.section, // For C-WAGS Rally
        totalDeductions: getDeductions(row.rally_obedience),
        time: formatTimeWithMs(
          row.rally_obedience?.time,
          row.rally_obedience?.result_type
        ),
        score: calculateObedienceScore(
          row.rally_obedience,
          Constants.show_type.rally_obedience,
          row.dog_class?.level
        ),
        jumpHeight: formatJumpInches(row.rally_jump_height),
      };
    case Constants.show_type.scentwork: {
      // Calculate placement using all scores for the same element/level
      let placement = "";
      if (allScores) {
        const eligibleScores = allScores.filter(
          (s) =>
            s.show_type === Constants.show_type.scentwork &&
            s.dog_class?.level === row.dog_class?.level &&
            s.dog_class?.show_element === row.dog_class?.show_element
        );
        const placementInfo = getAkcScentworkPlacement(row, eligibleScores);
        placement = placementInfo.place;
      }

      return {
        result: getAkcScentworkResult(row),
        resultReason: getAkcScentworkFieldValue(row, "result_reason"),
        searchTime: getAkcScentworkFieldValue(row, "search_time"),
        faults: getAkcScentworkFaults(row),
        numFinds: getAkcScentworkFieldValue(row, "num_finds"),
        placement: placement,
      };
    }
    case Constants.show_type.nosework:
      return {
        section: row.dog_class?.section,
        resultReason: row.nosework?.result_reason,
        searchTime: row.nosework?.search_time || "",
        excludedFromPlacement: row.nosework?.excluded_from_placement,
      };
    case Constants.show_type.herding:
    case Constants.show_type.pre_trial_herding:
    case Constants.show_type.instinct_test_herding:
      return {
        section: row.dog_class?.section,
        elapsedTime: formatMMSS(row.herding?.elapsed_time ?? 0),
        placement: row.herding?.placement?.toString() || "N/A",
        score: row.herding?.score,
      };
    case Constants.show_type.fast_cat:
      return {
        timeSlot: row.time_slot || "",
        time: row.fastcat?.time,
      };
    case Constants.show_type.obedience_show_n_go:
    case Constants.show_type.general_rally:
      return {
        timeSlot: row.time_slot || "",
      };
    case Constants.show_type.farm_dog:
      return {
        judge: row.judge_name || "",
      };
    case Constants.show_type.fetch:
      return {};
    case Constants.show_type.urban_rat_race: {
      const urrLevel = row.dog_class?.level || "";
      const urrFaultFactor = [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
      ].includes(urrLevel)
        ? 10
        : 15;
      const urrRawTime = row.urban_rat_race?.time;
      const urrFaultCount = row.urban_rat_race?.fault_count ?? 0;
      const urrFinalTime =
        urrRawTime !== undefined
          ? urrRawTime + urrFaultFactor * urrFaultCount
          : undefined;
      return {
        section: row.dog_class?.section,
        time: formatRatRaceTime(urrRawTime),
        faultCount: urrFaultCount,
        ratsFound: row.urban_rat_race?.rats_found, // for "Fantastic Five" class
        finalTime: formatRatRaceTime(urrFinalTime),
      };
    }
    case Constants.show_type.country_rat_race: {
      const crrLevel = row.dog_class?.level || "";
      const crrFaultFactor = [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
      ].includes(crrLevel)
        ? 10
        : 15;
      const crrRawTime = row.country_rat_race?.time;
      const crrFaultCount = row.country_rat_race?.fault_count ?? 0;
      const crrFinalTime =
        crrRawTime !== undefined
          ? crrRawTime + crrFaultFactor * crrFaultCount
          : undefined;
      return {
        section: row.dog_class?.section,
        time: formatRatRaceTime(crrRawTime),
        faultCount: crrFaultCount,
        ratsFound: row.country_rat_race?.rats_found, // for "Fantastic Five" class
        finalTime: formatRatRaceTime(crrFinalTime),
      };
    }
    case Constants.show_type.lost_and_found: {
      const lfRawTime = row.lost_and_found?.time;
      const lfFaultCount = row.lost_and_found?.fault_count ?? 0;
      const lfFinalTime =
        lfRawTime !== undefined ? lfRawTime + 10 * lfFaultCount : undefined;
      return {
        section: row.dog_class?.section,
        time: formatRatRaceTime(lfRawTime),
        faultCount: lfFaultCount,
        finalTime: formatRatRaceTime(lfFinalTime),
      };
    }
    case Constants.show_type.dock_jumping:
      return {
        feet: row.dock_jumping?.feet,
        inches: row.dock_jumping?.inches,
      };
    case Constants.show_type.weight_pull:
      return {
        weightClass: row.weight_pull?.weight_class,
        dogWeight: row.weight_pull?.dog_weight,
        highestWeight: row.weight_pull?.highest_weight,
        percentPulled: formatWeightPullPercent(row.weight_pull?.percent_pulled),
        time: formatWeightPullTime(row.weight_pull?.time),
      };
    case Constants.show_type.water_dog:
      return {
        time: row.water_dog?.time,
      };
    case Constants.show_type.precision_coursing:
      return {
        searchTime: row.precision_coursing?.search_time || "",
      };
    case Constants.show_type.shed_dog:
      return {
        searchTime: formatTimeWithMs(row.shed_dog?.search_time),
      };
    case Constants.show_type.agility:
      if (event?.sanctioning_club === Constants.sanctioning_club.AKC) {
        return {
          timeToCompete: row.akc_agility?.search_time || "",
          courseTime: row.akc_agility?.course_time || "",
          totalFaults: row.akc_agility?.total_faults,
          placement: row.akc_agility?.placement?.toString() || "N/A",
          score: row.akc_agility?.score,
          jumpHeight: formatJumpInches(row.agility_jump_height),
        };
      }

      return {
        // 4-H Agility uses Division A/B sections (see Constants.dog_class_section.fh_agility_division_a/b)
        section: row.dog_class?.section,
        time: formatTimeWithMs(row.agility?.time, row.agility?.result_type),
        score:
          row.agility?.result_type === Constants.score_result_type.qualified
            ? (row.agility?.score ?? "")
            : "",
        faults: row.agility?.faults ?? "",
        timeFaults: row.agility?.time_faults ?? "",
        excludedFromPlacement: row.agility?.excluded_from_placement,
      };
    case Constants.show_type.scent:
      return {
        section: row.dog_class?.section === Constants.dog_class_section.feo
          ? Constants.dog_class_section.feo
          : "",
        feo: row.dog_class?.section === Constants.dog_class_section.feo ? "Yes" : "",
        scents: getScentsString(row?.scent),
        fault1: row.scent?.fault1,
        fault2: row.scent?.fault2,
        searchTime: row.scent?.search_time,
      };
    case Constants.show_type.cwags_games:
      return {
        section: row.dog_class?.section === Constants.dog_class_section.feo
          ? Constants.dog_class_section.feo
          : "",
        feo: row.dog_class?.section === Constants.dog_class_section.feo ? "Yes" : "",
      };

    case Constants.show_type.scent_dog_games: {
      let sdgPlacement = "";
      if (allScores) {
        const eligibleScores = allScores.filter(
          (s) =>
            s.show_type === Constants.show_type.scent_dog_games &&
            s.dog_class?.level === row.dog_class?.level &&
            s.dog_class?.show_element === row.dog_class?.show_element &&
            s.dog_class?.section === row.dog_class?.section
        );
        const placementInfo = abiScentDogGamesGetPlacement(row, eligibleScores);
        sdgPlacement = placementInfo.place;
      }

      const sdg = row.scent_dog_games;
      return {
        section: row.dog_class?.section || "",
        result: abiScentDogGamesGetResultLabel(row),
        faultCount: sdg?.fault_count !== undefined ? sdg.fault_count : "",
        searchTime: sdg?.search_time || "",
        effectiveTime: sdg ? formatSDGEffectiveTime(sdg) : "",
        placement: sdgPlacement,
      };
    }

    case Constants.show_type.barn_hunt: {
      const isCrazy8s =
        row.dog_class?.level ===
        Constants.dog_class_element_level.barn_hunt_crazy_8s;
      const crazy8Total = isCrazy8s
        ? ((row.barn_hunt?.crazy8_score ?? 0) +
            (row.barn_hunt?.crazy8_bonus ?? 0)) ||
          ""
        : "";
      const placement = allScores ? bhCalculatePlacement(row, allScores) : "";
      const heightDivision = bhGetHeightDivision(row.height_at_withers);
      return {
        section: row.dog_class?.section,
        heightDivision,
        time: row.barn_hunt?.time || "",
        score: crazy8Total,
        crazy8_score: row.barn_hunt?.crazy8_score,
        crazy8_bonus: row.barn_hunt?.crazy8_bonus,
        placement,
        remx_flag: row.barn_hunt?.remx_flag,
      };
    }

    default:
      return {};
  }
};
